import { useState } from 'react';
import { Modal, Box, Button, TextField, InputAdornment, IconButton } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import '../translations.js';

const ChangePasswordModal = ({ open, onClose, userEmail }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push(t('password_min_8_chars'));
    }
    if (!/[a-z]/.test(password)) {
      errors.push(t('password_lowercase_required'));
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t('password_uppercase_required'));
    }
    return errors;
  };

  const handleSubmit = async () => {
    setError('');

    // Validate new password criteria
    const validationErrors = validatePassword(newPassword);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError(t('passwords_do_not_match'));
      return;
    }

    // Check if new password is different from current
    if (currentPassword === newPassword) {
      setError(t('new_password_must_be_different'));
      return;
    }

    setLoading(true);

    try {
      // First verify the current password
      const verifyRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: currentPassword,
        }),
      });

      if (!verifyRes.ok) {
        const verifyData = await verifyRes.json();
        setError(verifyData.detail || t('current_password_incorrect'));
        setLoading(false);
        return;
      }

      // Update password
      const userId = localStorage.getItem('user_id');
      const updateRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hashed_password: newPassword,
        }),
      });

      if (!updateRes.ok) {
        throw new Error(t('cpw_update_failed'));
      }

      // A password change revokes every other session (server-side
      // token_version bump). The backend hands back a fresh token for THIS
      // session so the user isn't logged out of the tab they changed it in.
      try {
        const updateData = await updateRes.json();
        if (updateData && typeof updateData.token === 'string') {
          localStorage.setItem('token', updateData.token);
        }
      } catch (e) {
        // Body not JSON / no token: the next request 401s and re-logs in,
        // which is still correct — just less smooth.
      }

      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Password change error:', err);
      setError(t('error_changing_password'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const isSubmitDisabled = !currentPassword || !newPassword || !confirmPassword || loading;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2.5 : 4,
          width: isMobile ? '80%' : '90%',
          maxWidth: 450,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>{t('change_password')}</h3>

        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <p style={{ color: '#22c55e', fontWeight: 500 }}>{t('password_changed_successfully')}</p>
          </Box>
        ) : (
          <>
            {error && (
              <Box
                sx={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <p style={{ color: '#dc2626', margin: 0, fontSize: '14px' }}>{error}</p>
              </Box>
            )}

            <form>
              <TextField
                fullWidth
                label={t('current_password')}
                type={showCurrentPassword ? 'text' : 'password'}
                variant="outlined"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        size="small"
                      >
                        <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} style={{ fontSize: '16px' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                    borderColor: '#515151',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'gray',
                    '&.Mui-focused': { color: '#5616ea' },
                  },
                }}
              />

              <TextField
                fullWidth
                label={t('new_password')}
                type={showNewPassword ? 'text' : 'password'}
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="new-password"
                helperText={t('password_requirements')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        size="small"
                      >
                        <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} style={{ fontSize: '16px' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                    borderColor: '#515151',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'gray',
                    '&.Mui-focused': { color: '#5616ea' },
                  },
                }}
              />

              <TextField
                fullWidth
                label={t('confirm_new_password')}
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} style={{ fontSize: '16px' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                    borderColor: '#515151',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'gray',
                    '&.Mui-focused': { color: '#5616ea' },
                  },
                }}
              />
            </form>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                disabled={loading}
                sx={{
                  color: '#5616ea',
                  borderColor: '#5616ea',
                  '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                }}
              >
                {t('cancel')}
              </Button>

              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={isSubmitDisabled}
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                  '&:disabled': { backgroundColor: '#9ca3af' },
                }}
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : t('change_password')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ChangePasswordModal;
